import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { 
  X, 
  RefreshCw, 
  UserCheck, 
  UserX, 
  AlertTriangle,
  ArrowRight
} from "lucide-react";

/**
 * SwapRequestDialog - KJ can request to swap out of an assigned booking
 * Two options:
 * 1. "I have found a replacement" - Select another KJ
 * 2. "I cannot find a replacement" - Withdraw (becomes urgent)
 */
const SwapRequestDialog = ({ 
  isOpen, 
  onClose, 
  booking,
  onSwapRequested
}) => {
  const [swapType, setSwapType] = useState(null); // "found_replacement" | "no_replacement"
  const [kjList, setKjList] = useState([]);
  const [selectedKjId, setSelectedKjId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingKjs, setLoadingKjs] = useState(false);

  useEffect(() => {
    if (isOpen && swapType === "found_replacement") {
      fetchKjList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, swapType]);

  const fetchKjList = async () => {
    setLoadingKjs(true);
    try {
      const response = await axios.get(`${API}/kj-list`);
      // Filter out the current KJ from the list
      const filteredKjs = (response.data.kj_details || []).filter(
        kj => kj.member_id !== booking?.kj_id
      );
      setKjList(filteredKjs);
    } catch (error) {
      toast.error("Failed to load KJ list");
    } finally {
      setLoadingKjs(false);
    }
  };

  const handleSubmit = async () => {
    if (!swapType) {
      toast.error("Please select an option");
      return;
    }

    if (swapType === "found_replacement" && !selectedKjId) {
      toast.error("Please select a replacement KJ");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        booking_code: booking.booking_code,
        swap_type: swapType,
        ...(swapType === "found_replacement" && { replacement_kj_id: selectedKjId })
      };

      await axios.post(`${API}/kj/request-swap`, payload);

      if (swapType === "found_replacement") {
        toast.success("Swap request submitted for Admin approval");
      } else {
        toast.success("Withdrawal confirmed. Admin has been notified.");
      }

      onSwapRequested && onSwapRequested();
      handleClose();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit swap request";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSwapType(null);
    setSelectedKjId("");
    setKjList([]);
    onClose();
  };

  if (!isOpen || !booking) return null;

  const getSlotTime = (slotType) => {
    return slotType === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-4 border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-purple-600 text-white p-4 flex justify-between items-center border-b-4 border-black">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <RefreshCw size={24} />
            Request Swap
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-purple-700 rounded"
            data-testid="close-swap-dialog"
          >
            <X size={24} />
          </button>
        </div>

        {/* Booking Info */}
        <div className="p-4 bg-purple-50 border-b-2 border-black">
          <p className="text-sm text-gray-600 mb-2">You are requesting to swap out of:</p>
          <div className="space-y-1">
            <p className="font-bold text-lg notranslate" translate="no">{booking.booking_code}</p>
            <p className="text-sm"><strong>Date:</strong> {booking.date}</p>
            <p className="text-sm"><strong>Time:</strong> {getSlotTime(booking.slot_type)}</p>
            <p className="text-sm"><strong>Resident:</strong> <span className="notranslate" translate="no">{booking.resident_name || booking.member_name}</span></p>
          </div>
        </div>

        {/* Options */}
        <div className="p-4 space-y-4">
          <p className="font-bold text-gray-700">Select an option:</p>

          {/* Option 1: Found Replacement */}
          <button
            onClick={() => setSwapType("found_replacement")}
            className={`w-full p-4 border-2 text-left transition-all ${
              swapType === "found_replacement" 
                ? "border-green-500 bg-green-50" 
                : "border-gray-300 hover:border-green-400 hover:bg-green-50"
            }`}
            data-testid="swap-option-found-replacement"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                swapType === "found_replacement" ? "bg-green-500 text-white" : "bg-gray-200"
              }`}>
                <UserCheck size={20} />
              </div>
              <div>
                <p className="font-bold">I have found a replacement</p>
                <p className="text-sm text-gray-600">Select another KJ to take over this session</p>
              </div>
            </div>
          </button>

          {/* KJ Selection (shown when option 1 is selected) */}
          {swapType === "found_replacement" && (
            <div className="ml-4 p-4 bg-gray-50 border-l-4 border-green-500">
              <label className="block text-sm font-bold mb-2">
                Select Replacement KJ <span className="text-red-500">*</span>
              </label>
              {loadingKjs ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : kjList.length === 0 ? (
                <p className="text-sm text-gray-500">No other KJs available</p>
              ) : (
                <select
                  value={selectedKjId}
                  onChange={(e) => setSelectedKjId(e.target.value)}
                  className="w-full p-3 border-2 border-black"
                  data-testid="replacement-kj-select"
                >
                  <option value="">-- Select a KJ --</option>
                  {kjList.map(kj => (
                    <option key={kj.member_id} value={kj.member_id}>
                      {kj.name} ({kj.type})
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-2">
                The swap will require Admin approval before it takes effect.
              </p>
            </div>
          )}

          {/* Option 2: No Replacement */}
          <button
            onClick={() => setSwapType("no_replacement")}
            className={`w-full p-4 border-2 text-left transition-all ${
              swapType === "no_replacement" 
                ? "border-red-500 bg-red-50" 
                : "border-gray-300 hover:border-red-400 hover:bg-red-50"
            }`}
            data-testid="swap-option-no-replacement"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                swapType === "no_replacement" ? "bg-red-500 text-white" : "bg-gray-200"
              }`}>
                <UserX size={20} />
              </div>
              <div>
                <p className="font-bold">I cannot find a replacement</p>
                <p className="text-sm text-gray-600">Withdraw from this session (Admin will be notified urgently)</p>
              </div>
            </div>
          </button>

          {/* Warning for No Replacement */}
          {swapType === "no_replacement" && (
            <div className="ml-4 p-4 bg-red-50 border-l-4 border-red-500">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-red-800">Warning</p>
                  <p className="text-sm text-red-700">
                    This will immediately mark the booking as <strong>URGENT</strong> with no KJ assigned.
                    The Admin will be notified to find a replacement.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-100 border-t-2 border-black flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 brutal-btn-outline"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !swapType || (swapType === "found_replacement" && !selectedKjId)}
            className={`flex-1 brutal-btn-primary flex items-center justify-center gap-2 ${
              loading ? "opacity-50" : ""
            }`}
            data-testid="submit-swap-request"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Processing...
              </>
            ) : (
              <>
                Confirm <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwapRequestDialog;
